import { Outlet, useLocation, useMatches } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { RouteTitleHandler, TitleHandler } from '@/types';
import { useAuth, isAdministrator, canSwitchFacility } from '@/auth/useAuth';
import TopNav from '@/components/navigation/TopNav';
import Sidebar from '@/components/navigation/Sidebar';
import MobileNav from '@/components/navigation/MobileNav';
import Breadcrumbs from '@/components/navigation/Breadcrumbs';
import { TitleManager } from '@/components/TitleManager';
import UnlockEdTour from '@/components/UnlockEdTour';
import HelpCenter from '@/pages/HelpCenter';
import { usePageTitle } from '@/contexts/usePageTitle';
import { useBreadcrumb } from '@/contexts/useBreadcrumb';
import { useBreadcrumbsFromRoutes } from '@/hooks/useBreadcrumbsFromRoutes';
import { resolveTitle } from '@/loaders/routeLoaders';

import WebsocketSession from '@/session/websocket';

export default function AuthenticatedLayout() {
    const { user } = useAuth();
    const [helpCenterOpen, setHelpCenterOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const matches = useMatches();
    const location = useLocation();
    const currentRoute = matches[matches.length - 1];
    const routeData = currentRoute?.data as TitleHandler;
    const routeHandle = currentRoute?.handle as RouteTitleHandler<TitleHandler>;
    const pageTitle = resolveTitle(routeHandle, routeData);
    const { setPageTitle } = usePageTitle();
    const { breadcrumbItems: contextBreadcrumbs } = useBreadcrumb();
    const routeBreadcrumbs = useBreadcrumbsFromRoutes();
    const breadcrumbItems =
        routeBreadcrumbs.length > 0 ? routeBreadcrumbs : contextBreadcrumbs;
    const isProgramDetail = /^\/programs\/\d+$/.test(location.pathname);
    const isResidentProfile = /^\/residents\/\w+$/.test(location.pathname);
    const isClassDetail = /^\/program-classes\/\d+\/detail$/.test(
        location.pathname
    );
    const isEventAttendance =
        /^\/program-classes\/\d+\/events\/\d+\/attendance\//.test(
            location.pathname
        );
    const isClassesPage = location.pathname === '/classes';
    const isResidentsPage = location.pathname === '/residents';
    const isDashboard = location.pathname.startsWith('/dashboard');
    const isProgramsList = location.pathname === '/programs';
    const isFacilities = location.pathname === '/facilities';
    const isSchedule = location.pathname === '/schedule';
    const isOperationalInsights = location.pathname === '/operational-insights';
    const isAdmins = location.pathname === '/admins';
    const isKnowledgeCenter =
        location.pathname === '/knowledge-center-management' ||
        location.pathname === '/knowledge-center';
    const isResidentKnowledgeCenter = location.pathname === '/knowledge-center';
    const isLearningRecord =
        location.pathname.startsWith('/learning-record-funnel') ||
        location.pathname.startsWith('/learning-record-categories');
    const isContentViewer = location.pathname.startsWith('/viewer/');
    const isResidentPage = [
        '/learning-path',
        '/my-courses',
        '/my-progress',
        '/resident-programs',
        '/home'
    ].includes(location.pathname);
    const isFullBleed =
        isProgramDetail ||
        isResidentProfile ||
        isResidentsPage ||
        isClassDetail ||
        isEventAttendance ||
        isClassesPage ||
        isDashboard ||
        isProgramsList ||
        isFacilities ||
        isKnowledgeCenter ||
        isLearningRecord ||
        isContentViewer ||
        isResidentPage ||
        isSchedule ||
        isOperationalInsights;
    const fullBleedWrapperClass =
        isDashboard ||
        isProgramsList ||
        isFacilities ||
        isProgramDetail ||
        isResidentProfile ||
        isResidentsPage ||
        isClassDetail ||
        isClassesPage ||
        isKnowledgeCenter ||
        isLearningRecord ||
        isContentViewer ||
        isResidentPage ||
        isSchedule ||
        isOperationalInsights
            ? 'py-0'
            : 'py-4';
    const showBreadcrumbs =
        breadcrumbItems.length > 0 &&
        !isProgramDetail &&
        !isResidentProfile &&
        !isContentViewer;
    const isFacilityView =
        isProgramDetail &&
        user !== undefined &&
        canSwitchFacility(user) &&
        new URLSearchParams(location.search).has('facility_id');

    useEffect(() => {
        if (!user) return;
        if (isContentViewer) {
            setPageTitle('');
        } else if (isProgramDetail) {
            if (isFacilityView) {
                setPageTitle('');
            } else if (canSwitchFacility(user)) {
                setPageTitle('Statewide Program View');
            } else {
                setPageTitle('Program Details');
            }
        } else if (pageTitle) {
            setPageTitle(pageTitle);
        }
    }, [
        isContentViewer,
        isFacilityView,
        isProgramDetail,
        pageTitle,
        setPageTitle,
        user
    ]);

    // The app scrolls this inner container, never the document, so it must
    // hold focus for keyboard scrolling (arrows/PageDown/Space) to work
    useEffect(() => {
        scrollContainerRef.current?.focus({ preventScroll: true });
    }, [location.pathname]);

    useEffect(() => {
        if (user && !isAdministrator(user)) {
            const ws = new WebsocketSession(user);
            ws.connect();
            window.websocket = ws;
            return () => {
                ws.tearDownConnection(true);
            };
        }
    }, [user]);

    if (!user) return null;

    const needsGrayBg =
        isResidentProfile ||
        isResidentsPage ||
        isClassesPage ||
        isFacilities ||
        isAdmins ||
        isKnowledgeCenter ||
        isOperationalInsights ||
        (isProgramDetail && canSwitchFacility(user));
    // Residents get no header bar at `md`+ (see the MobileNav branch below), so
    // pages that size to the viewport must not reserve 4rem for it there
    const headerOffsetClass = isAdministrator(user)
        ? ''
        : 'app-header-mobile-only';
    const rootClass = `h-screen bg-background flex overflow-hidden ${headerOffsetClass}`;
    const contentClass = `flex-1 min-h-full ${isResidentKnowledgeCenter ? 'overflow-hidden' : 'overflow-y-auto'} overflow-x-hidden ${needsGrayBg ? 'bg-[#E2E7EA]' : 'bg-[#E2E7EA]'}`;

    return (
        <div className={rootClass}>
            <div className="hidden md:flex">
                <Sidebar
                    collapsed={sidebarCollapsed}
                    onToggleCollapse={() =>
                        setSidebarCollapsed(!sidebarCollapsed)
                    }
                    onToggleHelpCenter={() =>
                        setHelpCenterOpen(!helpCenterOpen)
                    }
                />
            </div>

            <div className="flex flex-1 min-w-0 flex-col overflow-hidden">
                {isAdministrator(user) ? (
                    <div className="flex items-center shrink-0 h-16 border-b border-border">
                        <div className="md:hidden px-2">
                            <MobileNav />
                        </div>
                        <div className="flex-1">
                            <TopNav />
                        </div>
                    </div>
                ) : (
                    <div className="md:hidden flex items-center shrink-0 h-16 border-b border-border px-2">
                        <MobileNav />
                    </div>
                )}

                <TitleManager />
                <UnlockEdTour />

                <div
                    ref={scrollContainerRef}
                    tabIndex={-1}
                    className={`flex min-w-0 flex-1 overflow-hidden overflow-y-auto outline-none`}
                >
                    <div
                        className={`flex-1 min-w-0 transition-all duration-300 ease-in-out ${helpCenterOpen ? 'max-w-[calc(100%-20rem)]' : ''}`}
                    >
                        <div className={contentClass}>
                            {isFullBleed ? (
                                isClassDetail || isEventAttendance ? (
                                    <Outlet />
                                ) : (
                                    <div
                                        className={`${fullBleedWrapperClass} h-full`}
                                    >
                                        {showBreadcrumbs && (
                                            <div className="max-w-7xl mx-auto px-6 mb-4">
                                                <Breadcrumbs
                                                    items={breadcrumbItems}
                                                />
                                            </div>
                                        )}
                                        <Outlet />
                                    </div>
                                )
                            ) : (
                                <div className="max-w-7xl mx-auto">
                                    {showBreadcrumbs && (
                                        <div className="mb-4">
                                            <Breadcrumbs
                                                items={breadcrumbItems}
                                            />
                                        </div>
                                    )}
                                    <Outlet />
                                </div>
                            )}
                        </div>
                    </div>

                    {helpCenterOpen && (
                        <div className="w-80 shrink-0 bg-background border-l border-border animate-in slide-in-from-right duration-300 flex flex-col sticky top-0 h-[calc(100vh-4rem)]">
                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 py-4">
                                <HelpCenter
                                    close={() => setHelpCenterOpen(false)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
